import { useState, useEffect, useContext } from "react";
import { useRouter } from "next/router";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Text,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Center,
  useDisclosure,
} from "@chakra-ui/react";
import { Page } from "components/page";
import { AiOutlineDelete } from "react-icons/ai";
import { BsPencil } from "react-icons/bs";
import { CreateEditList } from "components/list/create-edit-list";
import { ListTable } from "components/list/list-table";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { getAccount } from "utils/sessionHelper";
import { useSession } from "next-auth/react";
import toast from "react-hot-toast";
import { ConfirmPopup } from "components/popups/confirmPopup";
import { addRemovePeopleToList, deleteList } from "controllers/list";
import { AddPeople } from "components/popups/add-people";
import ProtectedRoute from "components/protected-route";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { isFeatureEnabled, setInLocalStorage } from "utils/helpers";
import { sendLeads } from "controllers/lemlist";
import { textStyle, variant } from "theme/names";
import { TweetContext } from "context/tweetContext";

const LAST_VIEWED_LIST = "lastViewedList";

export default function List({ query }) {
  const [lists, setLists] = useState<any>([]);
  const [selectedListId, setSelectedListId] = useState<number | undefined>();
  const [refresh, refresher] = useState({});
  const [toDeleteListId, setToDeleteListId] = useState<number | undefined>();
  const [peopleRefresh, peopleRefresher] = useState({});
  const [mode, setMode] = useState<string>("");
  const [dataLoaderAdd, setDataLoaderAdd] = useState({});
  const [contactsLength, setContactsLength] = useState<number>(0);
  const [pageNo, setPageNo] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [contactLimits, setContactLimits] = useState(50);
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";

  const isAuthenticated = useFirebaseUser();
  const {
    isOpen: isCreateListOpen,
    onOpen: onCreateListOpen,
    onClose: onCreateListClosed,
  } = useDisclosure();
  const {
    isOpen: isAddPeopleOpen,
    onOpen: onAddPeopleOpen,
    onClose: onAddPeopleClose,
  } = useDisclosure();
  const {
    isOpen: isDeletePeopleOpen,
    onOpen: onDeletePeopleOpen,
    onClose: onDeletePeopleClose,
  } = useDisclosure();
  const miscellaneousContext: any = useMiscellaneousContext();
  const router = useRouter();

  const listIsNotEmpty = lists?.length > 0;

  useEffect(() => {
    if (isAuthenticated) {
      let contactLists = getAccount(session)?.contactLists ?? [];

      if (!isFeatureEnabled("contactLists", session, {}, false, "")) {
        console.log(contactLists, "contactLists");
        contactLists = contactLists.filter(
          (contactList) => contactList.id !== 0 && contactList.id !== 1
        );
      }



      if (contactLists?.length > 0) {
        contactLists.forEach((contact) => {
          if (contact.createdAt?._seconds) {
            contact.createdAt = new Date(contact.createdAt._seconds * 1000);
          }
        });
        contactLists.sort(
          (a, b) => getTime(b.createdAt) - getTime(a.createdAt)
        );
        setLists(contactLists);

        const listPageNoQuery = parseInt(query.page) || 1;
        const listIdInQuery = parseInt(query.list);
        const exist = contactLists.find((c) => c.id === listIdInQuery);

        setPageNo(listPageNoQuery);
        // @ts-ignore
        if (listIdInQuery !== NaN && exist) {
          setSelectedListId(listIdInQuery);
          setInLocalStorage(LAST_VIEWED_LIST, listIdInQuery);
        } else if (selectedListId === undefined) {
          let listId = JSON.parse(
            JSON.stringify(localStorage.getItem(LAST_VIEWED_LIST) ?? "")
          );

          if (!contactLists.some((c) => c.id === parseInt(listId))) {
            listId = contactLists[0].id;
          }
          setSelectedListId(parseInt(listId));
          setInLocalStorage(LAST_VIEWED_LIST, parseInt(listId));
          router.push({
            pathname: "contacts",
            query: { list: listId, page: listPageNoQuery },
          });
        }
      } else {
        setLists([]);
        setPageNo(1);
        setSelectedListId(undefined);
        localStorage.removeItem(LAST_VIEWED_LIST);
        router.push({
          pathname: "contacts",
        });
      }
    }
  }, [session, isAuthenticated, refresh]);

  useEffect(() => {
    if (parseInt(query.list) !== selectedListId) {
      router.push({
        pathname: "contacts",
        query: { list: query.list, page: 1 },
      });
      refresher({}); //can be removed. Keeping it because, it might be linked with something else?
    }
  }, [query.list]);

  useEffect(() => {
    if (parseInt(query.page) !== pageNo && contactsLength === 0) {
      router.push({
        pathname: "contacts",
        query: { list: query.list, page: 1 },
      });
    } else if (
      parseInt(query.page) !== pageNo &&
      Object.keys(query)?.length <= 2
    ) {
      refresher({});
    }
  }, [query.page, pageNo, contactsLength]);

  useEffect(() => {
    setTotalPages(Math.ceil(contactsLength / contactLimits));
  }, [totalPages, contactLimits, contactsLength]);

  // turn this on incase you wanna close sidebar on navigating to different page
  // useEffect(() => {
  //   return () => {
  //     miscellaneousContext.setIsPersonSidebarOpen(false);
  //     miscellaneousContext.setSidebarPerson(null);
  //   };
  // }, []);

  const handleDelete = async () => {
    try {
      if (!isAuthenticated) {
        toast.error("User is not authenticated to create list");
        return;
      }

      if (toDeleteListId === undefined) {
        return;
      }

      miscellaneousContext.setSidebarPerson(null);
      await deleteList(session, toDeleteListId);
      toast.success(`Successfully deleted`);

      if (selectedListId === toDeleteListId) {
        setSelectedListId(undefined);
        localStorage.removeItem(LAST_VIEWED_LIST);
      }

      setToDeleteListId(undefined);
      refresher({});
    } catch (err) {
      console.log("Error in deleting list: ", err);
      toast.error("Error in deleting list: ", err);
    }
  };

  const handleAdd = async (listOfPeople: any[], listId?: number) => {
    try {
      if (!isAuthenticated) {
        toast.error("User is not Authenticated");
        return;
      }

      await addRemovePeopleToList(
        getAccount(session)?.id,
        // @ts-ignore
        listId ?? selectedListId,
        listOfPeople,
        "save",
        setDataLoaderAdd
      );

      toast.success("Successfully added people to the list");

      // add to lemlist
      if (selectedList?.isAddAutoLemlist) {
        await sendLeads(session, selectedList.idLemlistCampaign, listOfPeople);
      }

      peopleRefresher({});
      onAddPeopleClose();
    } catch (err) {
      console.log("Error in adding people to the list: ", err);
      toast.error("Error in adding people to the list. " + err.message);
    }
  };

  const getTime = (date?: Date) => {
    return date != null ? date.getTime() : 0;
  };

  const selectedList = lists?.find((l) => l.id === selectedListId);
  const selectedListName =
    listIsNotEmpty &&
    selectedListId !== undefined &&
    lists?.filter((l) => l.id === selectedListId)[0]?.name;

  const tweetContext: any = useContext(TweetContext);

  if (!loading && !session) {
    return <ProtectedRoute />;
  }

  return (
    <Page
      {...{
        title: "Contacts",
        description: "Create lists of people to connect with your audience.",
      }}
    >
      <Box mt="0" maxWidth="1600">
        <Box
          mt="10"
          // filter={isAllowed(session?.user, "crm") ? "none" : "blur(3px)"}
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            direction={{ base: "column", lg: "row" }}
          >
            <Box>
              <Flex justifyContent="start" alignItems="center">
                <Menu isLazy variant={variant.Menu.secondary}>
                  <MenuButton
                    as={Button}
                    // size="sm"
                    variant="secondary"
                    rightIcon={<ChevronDownIcon />}
                    // maxW="44"
                  >
                    <Text
                      // textStyle={textStyle["body.medium.light"]}
                      noOfLines={1}
                      wordBreak={"break-all"}
                    >
                      {listIsNotEmpty ? selectedListName : "No List found"}
                    </Text>
                  </MenuButton>
                  <MenuList maxH="215px" overflow="scroll">
                    {listIsNotEmpty ? (
                      lists.map((list) => (
                        <MenuItem
                          width="100%"
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="default"
                          key={list.id}
                          onClick={() => {
                            router.push({
                              pathname: "contacts",
                              query: { list: list.id, page: pageNo },
                            });
                          }}
                        >
                          <Text textStyle={textStyle["body.medium.light"]}>
                            {list.name}
                          </Text>
                          {!list?.isLikers && !list?.isRepliers && (
                            <AiOutlineDelete
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setToDeleteListId(list.id);
                                onDeletePeopleOpen();
                              }}
                            />
                          )}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        cursor="default"
                      >
                        <Text textStyle={textStyle["body.medium.light"]}>
                          No List found
                        </Text>
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
                <Text ml="3">{contactsLength} people</Text>
                {!selectedList?.isLikers && !selectedList?.isRepliers && (
                  <IconButton
                    aria-label="edit list"
                    // size="sm"
                    ml="2"
                    // background="none"
                    variant="secondary"
                    icon={<BsPencil />}
                    isDisabled={selectedListId === undefined}
                    onClick={() => {
                      setMode("edit");
                      onCreateListOpen();
                    }}
                  />
                )}
                {!selectedList?.isLikers && !selectedList?.isRepliers && (
                  <IconButton
                    aria-label="delete list"
                    // size="sm"
                    ml="2"
                    variant={variant.Button.secondaryDestructive}
                    // background="none"
                    icon={<AiOutlineDelete />}
                    isDisabled={selectedListId === undefined}
                    onClick={() => {
                      setToDeleteListId(selectedListId);
                      onDeletePeopleOpen();
                    }}
                  />
                )}
                <Button
                  leftIcon={<AddIcon h="3" w="3" />}
                  // background="none"
                  ml="2"
                  // color="gray"
                  variant="secondary"
                  onClick={() => {
                    setMode("");
                    onCreateListOpen();
                  }}
                >
                  Create new list
                </Button>
              </Flex>
            </Box>
            <Box>
              <Button
                // size="sm"
                // colorScheme="blue"
                // color={useColorModeValue("blue.700", "blue.200")}
                variant="secondary"
                // mt={{ base: "4", lg: "0" }}
                leftIcon={<AddIcon h="3" w="3" />}
                isDisabled={selectedListId === undefined}
                onClick={onAddPeopleOpen}
              >
                Add people
              </Button>
            </Box>
          </Flex>
          {listIsNotEmpty ? (
            <ListTable
              ctaAddPeople={onAddPeopleOpen}
              listId={selectedListId}
              list={selectedList}
              pageNo={pageNo}
              selectedListName={selectedListName}
              setContactsLength={setContactsLength}
              contactLimits={contactLimits}
              refresh={peopleRefresh}
              openCreateList={() => {
                setMode("existing");
                onCreateListOpen();
              }}
            />
          ) : (
            <Box display="flex" justifyContent="center">
              <Center
                maxW="400px"
                mt={20}
                display="flex"
                flexDirection="column"
              >
                <Text textStyle={textStyle["body.medium.light"]}>
                  You have not created any list yet.
                </Text>
                <Button
                  mt={5}
                  maxW="300px"
                  // colorScheme="blue"
                  // bg="blue.700"
                  // color="white"
                  // _hover={{ bg: "blue.600" }}
                  variant="primary"
                  onClick={() => {
                    onCreateListOpen();
                  }}
                >
                  Create my first list
                </Button>
              </Center>
            </Box>
          )}
          {listIsNotEmpty && totalPages > 1 && contactsLength > 0 && (
            <Box
              p={3}
              w="full"
              display={"flex"}
              flexDirection={"row"}
              flexWrap={"nowrap"}
              alignItems={"center"}
              justifyContent={"right"}
              gap={"5"}
              mb={5}
            >
              <Button
                isActive={pageNo - 1 > 0}
                p={3}
                variant={pageNo - 1 > 0 ? "primary" : ""}
                onClick={(e) => {
                  router.push({
                    pathname: "contacts",
                    query: {
                      list: selectedListId,
                      page: pageNo - 1 > 0 ? pageNo - 1 : 1,
                    },
                  });
                }}
              >
                Prev Page
              </Button>
              <Text>
                Page {pageNo} of {totalPages}
              </Text>
              <Button
                isActive={pageNo < totalPages}
                p={3}
                variant={pageNo < totalPages ? "primary" : ""}
                onClick={(e) => {
                  router.push({
                    pathname: "contacts",
                    query: {
                      list: selectedListId,
                      page: pageNo + 1 <= totalPages ? pageNo + 1 : totalPages,
                    },
                  });
                }}
              >
                Next Page
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {/* {!isAllowed(session?.user, "crm") && <UpgradePopup />} */}
      <CreateEditList
        isOpen={isCreateListOpen}
        onClose={onCreateListClosed}
        contacts={miscellaneousContext.newListMembers}
        onCreation={async (newListId: number) => {
          if (!mode) {
            onAddPeopleOpen();
            router.push({
              pathname: "contacts",
              query: { list: newListId, page: 1 },
            });
          }
          if (mode === "existing") {
            await handleAdd(miscellaneousContext.newListMembers, newListId);
          }
          onCreateListClosed();
        }}
        lists={lists}
        mode={mode}
        // @ts-ignore
        selectedListId={selectedListId}
      />
      <AddPeople
        isOpen={isAddPeopleOpen}
        onClose={onAddPeopleClose}
        title={`Add People to ${selectedListName}`}
        onAdd={handleAdd}
        dataLoaderAdd={dataLoaderAdd}
        session={session}
      />
      <ConfirmPopup
        destructive
        isOpen={isDeletePeopleOpen}
        onClose={onDeletePeopleClose}
        title={`Deleting ${lists.find((l) => l.id === toDeleteListId)?.name}`}
        body="Are you sure? You can't undo this action afterwards."
        callback={handleDelete}
      />
    </Page>
  );
}

List.getInitialProps = async ({ query }) => {
  return { query };
};
